import { useLocalStorage } from '@uidotdev/usehooks'
import { useState, type ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible'
import { ChevronDownIcon } from 'lucide-react'

const STORAGE_KEY_PREFIX = 'design-form-collapsible-card'

const getStorageKey = (persistKey: string) => `${STORAGE_KEY_PREFIX}:${persistKey}`

type CollapsibleCardContentProps = {
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
}

type CollapsibleCardViewProps = CollapsibleCardContentProps & {
  isOpen: boolean | undefined
  onOpenChange: (open: boolean) => void
}

const CollapsibleCardView = ({
  title,
  description,
  children,
  isOpen,
  onOpenChange,
}: CollapsibleCardViewProps) => {
  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <Card className="py-0 gap-4">
        <CollapsibleTrigger asChild>
          <CardHeader className="relative group cursor-pointer hover:bg-muted/50 pt-6 pb-6">
            <div className="inline-flex w-full items-center">
              <CardTitle>{title}</CardTitle>
              <ChevronDownIcon className="ml-auto group-data-[state=open]:rotate-180" />
            </div>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent className="pb-6">
          <CardContent>{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

type UnpersistedCollapsibleCardProps = CollapsibleCardContentProps & {
  defaultOpen?: boolean
}

const UnpersistedCollapsibleCard = ({
  title,
  description,
  children,
  defaultOpen,
}: UnpersistedCollapsibleCardProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <CollapsibleCardView
      title={title}
      description={description}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      {children}
    </CollapsibleCardView>
  )
}

type PersistedCollapsibleCardProps = UnpersistedCollapsibleCardProps & {
  persistKey: string
}

const PersistedCollapsibleCard = ({
  title,
  description,
  children,
  defaultOpen,
  persistKey,
}: PersistedCollapsibleCardProps) => {
  const [isOpen, setIsOpen] = useLocalStorage(getStorageKey(persistKey), defaultOpen)

  return (
    <CollapsibleCardView
      title={title}
      description={description}
      isOpen={Boolean(isOpen)}
      onOpenChange={setIsOpen}
    >
      {children}
    </CollapsibleCardView>
  )
}

export type CollapsibleCardProps = UnpersistedCollapsibleCardProps & {
  /** When set, the open state is remembered in localStorage under this key. */
  persistKey?: string | null
}

export const CollapsibleCard = ({ persistKey = null, ...props }: CollapsibleCardProps) => {
  if (!persistKey) {
    return <UnpersistedCollapsibleCard {...props} />
  }

  return <PersistedCollapsibleCard persistKey={persistKey} {...props} />
}
